/**
 * Omnivaso Unified Payment Gateway Abstraction Layer
 * Standardizes checkout pipelines around extensible Payment Adapters:
 * - Simulated Card Sandbox
 * - Stripe Checkout API (Credit Cards)
 * - PayPal Express Checkout
 * - Square Web Payments
 */

/**
 * Card expiry helpers. Accepts MM/YY, MM-YY, MM YY, MM.YY, MMYY, MM/YYYY, M/YY, etc.
 * normalizeExpiry returns 'MM/YY' or null if unparsable / invalid month.
 */
export const normalizeExpiry = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return null;
    let month = null, year = null;
    // Delimited: 12/28, 12-28, 12.28, 12 28, 5/28, 5-28, 12/2028, etc.
    const m = s.match(/^(\d{1,2})\s*[\/\-.\s]\s*(\d{2}|\d{4})$/);
    if (m) {
        month = parseInt(m[1], 10);
        year = m[2].slice(-2);
    } else {
        // Pure digits: 1228, 0528, 528, 122028, 52028
        const d = s.replace(/\s+/g, '');
        if (/^\d{3,6}$/.test(d)) {
            if (d.length === 3) {
                month = parseInt(d.slice(0, 1), 10);
                year = d.slice(1);
            } else if (d.length === 4) {
                month = parseInt(d.slice(0, 2), 10);
                year = d.slice(2);
            } else if (d.length === 5) {
                month = parseInt(d.slice(0, 1), 10);
                year = d.slice(3);
            } else if (d.length === 6) {
                month = parseInt(d.slice(0, 2), 10);
                year = d.slice(4);
            }
        }
    }
    if (!month || month < 1 || month > 12) return null;
    if (!year || year.length !== 2) return null;
    return `${String(month).padStart(2, '0')}/${year}`;
};

export const isExpiryInPast = (raw) => {
    const n = normalizeExpiry(raw);
    if (!n) return true;
    const [mm, yy] = n.split('/').map(Number);
    const now = new Date();
    const curYY = now.getFullYear() % 100;
    const curMM = now.getMonth() + 1;
    return yy < curYY || (yy === curYY && mm < curMM);
};

export const formatExpiryInput = (raw) => {
    let v = String(raw || '');
    // Allow typing numbers, slash, dash, dot, space up to 7 characters
    return v.replace(/[^\d\/\-.\s]/g, '').slice(0, 7);
};

export class BasePaymentAdapter {
    constructor(id, name, icon) {
        this.id = id;
        this.name = name;
        this.icon = icon;
    }

    getId() {
        return this.id;
    }

    getName() {
        return this.name;
    }

    getIcon() {
        return this.icon;
    }

    isAvailable(paymentInfo) {
        return true;
    }

    getType() {
        return 'card';
    }

    validate(paymentData) {
        return { valid: true };
    }

    formatPayload(orderContext, paymentData) {
        return {
            paymentMethod: this.id,
            paymentDetails: {
                gateway: this.id
            }
        };
    }
}

export class SimulatedPaymentAdapter extends BasePaymentAdapter {
    constructor() {
        super('simulated', 'Simulated Card Sandbox', '🧪');
    }

    isAvailable(paymentInfo) {
        // Explicitly enabled via toggle
        const isExplicitlyEnabled = 
            paymentInfo?.enable_simulated === true || 
            paymentInfo?.enable_simulated === 'true' || 
            paymentInfo?.enable_simulated === 1 || 
            paymentInfo?.enable_simulated === '1';
        if (isExplicitlyEnabled) return true;

        // Active gateway is simulated sandbox
        if (paymentInfo?.gateway === 'simulated') return true;

        // Fresh install / unconfigured store
        if (paymentInfo?.configured === false) return true;

        // If no credentials exist for any live gateway, sandbox must always be available
        const hasLiveGateway = Boolean(
            (paymentInfo?.stripe_public && paymentInfo?.enable_stripe !== false && paymentInfo?.enable_stripe !== 'false') ||
            (paymentInfo?.square_application_id && paymentInfo?.enable_square !== false && paymentInfo?.enable_square !== 'false') ||
            (paymentInfo?.paypal_client_id && paymentInfo?.enable_paypal !== false && paymentInfo?.enable_paypal !== 'false')
        );
        if (!hasLiveGateway) return true;

        return false;
    }

    getType() {
        return 'card';
    }

    validate(paymentData) {
        const { cardNumber, cardExpiry, cardCvv } = paymentData || {};
        const clean = (cardNumber || '').replace(/\s+/g, '');
        if (!clean || clean.length < 13) {
            return { valid: false, error: 'Please enter a valid test card number (e.g. 4242 4242 4242 4242).' };
        }
        if (!normalizeExpiry(cardExpiry)) {
            return { valid: false, error: 'Please enter a test expiry date (e.g. MM/YY, MM-YY, or MMYY).' };
        }
        if (!cardCvv || cardCvv.length < 3) {
            return { valid: false, error: 'Please enter a 3 or 4-digit CVV (e.g. 123).' };
        }
        return { valid: true };
    }

    formatPayload(orderContext, paymentData) {
        const cleanCard = (paymentData.cardNumber || '').replace(/\s+/g, '');
        return {
            paymentMethod: 'simulated',
            paymentDetails: {
                gateway: 'simulated',
                cardNumber: cleanCard,
                last4: cleanCard.slice(-4),
                expiry: normalizeExpiry(paymentData.cardExpiry) || paymentData.cardExpiry,
                cvv: paymentData.cardCvv || ''
            }
        };
    }
}

export class StripePaymentAdapter extends BasePaymentAdapter {
    constructor() {
        super('stripe', 'Stripe', '💳');
    }

    isAvailable(paymentInfo) {
        const isDisabled = 
            paymentInfo?.enable_stripe === false || 
            paymentInfo?.enable_stripe === 'false' || 
            paymentInfo?.enable_stripe === 0 || 
            paymentInfo?.enable_stripe === '0';
        if (isDisabled) return false;

        const hasKey = Boolean(paymentInfo?.stripe_public && String(paymentInfo.stripe_public).trim()) || paymentInfo?.stripe_ready === true;
        const isCurrent = paymentInfo?.gateway === 'stripe';
        if (paymentInfo?.hide_unconfigured !== false) {
            return isCurrent || hasKey;
        }
        return true;
    }

    getType(paymentInfo) {
        // If hosted mode (default), redirects to Stripe Hosted Checkout
        // If direct mode, shows on-page Card Number, Expiration, and CVV fields
        return paymentInfo?.stripe_checkout_mode === 'direct' ? 'card' : 'hosted';
    }

    validate(paymentData) {
        // In hosted mode, customer safely pays on Stripe's hosted checkout page
        if (paymentData?.stripe_checkout_mode !== 'direct') {
            return { valid: true };
        }
        // In direct mode, validate on-page card fields
        const { cardNumber, cardExpiry, cardCvv } = paymentData || {};
        const clean = (cardNumber || '').replace(/\s+/g, '');
        if (!clean || clean.length < 13) {
            return { valid: false, error: 'Please enter a valid 16-digit credit card number.' };
        }
        if (!normalizeExpiry(cardExpiry)) {
            return { valid: false, error: 'Please enter a valid card expiration date (MM/YY, MM-YY, or MMYY).' };
        }
        if (isExpiryInPast(cardExpiry)) {
            return { valid: false, error: 'The card expiration date is in the past.' };
        }
        if (!cardCvv || cardCvv.length < 3) {
            return { valid: false, error: 'Please enter a valid 3 or 4-digit security code (CVV).' };
        }
        return { valid: true };
    }

    formatPayload(orderContext, paymentData) {
        const cleanCard = (paymentData.cardNumber || '').replace(/\s+/g, '');
        return {
            paymentMethod: 'stripe',
            paymentDetails: {
                gateway: 'stripe',
                stripePublicKey: paymentData.stripe_public || '',
                cardNumber: cleanCard,
                last4: cleanCard.slice(-4),
                expiry: normalizeExpiry(paymentData.cardExpiry) || paymentData.cardExpiry,
                cvv: paymentData.cardCvv || ''
            }
        };
    }
}

export class PayPalPaymentAdapter extends BasePaymentAdapter {
    constructor() {
        super('paypal', 'PayPal Express Checkout', '🅿️');
    }

    isAvailable(paymentInfo) {
        const isDisabled = 
            paymentInfo?.enable_paypal === false || 
            paymentInfo?.enable_paypal === 'false' || 
            paymentInfo?.enable_paypal === 0 || 
            paymentInfo?.enable_paypal === '0';
        if (isDisabled) return false;

        const hasKey = Boolean(paymentInfo?.paypal_client_id && String(paymentInfo.paypal_client_id).trim()) || paymentInfo?.paypal_ready === true;
        const isCurrent = paymentInfo?.gateway === 'paypal';
        if (paymentInfo?.hide_unconfigured !== false) {
            return isCurrent || hasKey;
        }
        return true;
    }

    getType() {
        return 'hosted'; // PayPal uses redirect to PayPal checkout
    }

    validate(paymentData) {
        return { valid: true };
    }

    formatPayload(orderContext, paymentData) {
        return {
            paymentMethod: 'paypal',
            paymentDetails: {
                gateway: 'paypal',
                paypalClientId: paymentData.paypal_client_id || '',
                environment: paymentData.paypal_environment || 'sandbox'
            }
        };
    }
}

export class SquarePaymentAdapter extends BasePaymentAdapter {
    constructor() {
        super('square', 'Square Web Payments', '⬛');
    }

    isAvailable(paymentInfo) {
        const isDisabled = 
            paymentInfo?.enable_square === false || 
            paymentInfo?.enable_square === 'false' || 
            paymentInfo?.enable_square === 0 || 
            paymentInfo?.enable_square === '0';
        if (isDisabled) return false;

        const hasKey = Boolean(paymentInfo?.square_application_id && String(paymentInfo.square_application_id).trim()) || paymentInfo?.square_ready === true;
        const isCurrent = paymentInfo?.gateway === 'square';
        if (paymentInfo?.hide_unconfigured !== false) {
            return isCurrent || hasKey;
        }
        return true;
    }

    getType() {
        return 'card';
    }

    validate(paymentData) {
        const { cardNumber, cardExpiry, cardCvv } = paymentData || {};
        const clean = (cardNumber || '').replace(/\s+/g, '');
        if (!clean || clean.length < 13) {
            return { valid: false, error: 'Please enter a valid card number.' };
        }
        if (!normalizeExpiry(cardExpiry)) {
            return { valid: false, error: 'Please enter a valid expiry date (MM/YY, MM-YY, or MMYY).' };
        }
        if (isExpiryInPast(cardExpiry)) {
            return { valid: false, error: 'The card expiration date is in the past.' };
        }
        if (!cardCvv || cardCvv.length < 3) {
            return { valid: false, error: 'Please enter a valid CVV.' };
        }
        return { valid: true };
    }

    formatPayload(orderContext, paymentData) {
        const cleanCard = (paymentData.cardNumber || '').replace(/\s+/g, '');
        return {
            paymentMethod: 'square',
            paymentDetails: {
                gateway: 'square',
                locationId: paymentData.square_location_id || '',
                cardNumber: cleanCard,
                last4: cleanCard.slice(-4),
                expiry: normalizeExpiry(paymentData.cardExpiry) || paymentData.cardExpiry,
                cvv: paymentData.cardCvv || ''
            }
        };
    }
}

export class PaymentAdapterRegistry {
    constructor() {
        this.adapters = new Map();
        this.registerDefaultAdapters();
    }

    registerDefaultAdapters() {
        this.register(new SimulatedPaymentAdapter());
        this.register(new StripePaymentAdapter());
        this.register(new PayPalPaymentAdapter());
        this.register(new SquarePaymentAdapter());
    }

    register(adapter) {
        if (adapter && adapter.getId) {
            this.adapters.set(adapter.getId(), adapter);
        }
    }

    getAdapter(id) {
        return this.adapters.get(id) || this.adapters.get('simulated');
    }

    getAvailableAdapters(paymentInfo) {
        const activeGatewayId = paymentInfo?.gateway || 'simulated';
        const list = Array.from(this.adapters.values()).filter(adapter => adapter.isAvailable(paymentInfo));

        // Always guarantee at least one adapter: fallback to simulated sandbox if all were filtered out
        if (list.length === 0) {
            const sim = this.adapters.get('simulated');
            if (sim) list.push(sim);
        }

        return list.sort((a, b) => {
            if (a.getId() === activeGatewayId) return -1;
            if (b.getId() === activeGatewayId) return 1;
            return 0;
        });
    }
}

export const paymentRegistry = new PaymentAdapterRegistry();
